import {
  ChatBubbleOutline as ChatIcon,
  Close as CloseIcon,
  Pause as PauseIcon,
  PlayArrow as PlayIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Slider,
  TextField,
  Typography,
} from '@mui/material';
import axios from 'axios';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ChatBottomNav from '../components/chat/ChatBottomNav';
import ConfirmDialog from '../components/ConfirmDialog';
import AppHeader from '../components/layout/AppHeader';
import Toast from '../components/Toast';
import { API_ENDPOINTS } from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import { useConfirm } from '../hooks/useConfirm';
import { useToast } from '../hooks/useToast';

interface ActionProgress {
  id: string;
  score: number;
  check_in_date: string;
  notes: string;
}

interface Action {
  id: string;
  title: string;
  description: string;
  frequency: string;
  scheduled_days: number[];
  check_in_time: string;
  target_score: number;
  average_score: number;
  completion_rate: number;
  is_due_today: boolean;
  latest_progress: ActionProgress | null;
}

interface HabitDetail {
  id: string;
  title: string;
  description: string;
  category: string;
  category_display: string;
  target_timeframe: string;
  timeframe_display: string;
  status: string;
  status_display: string;
  priority: number;
  rationale: string;
  source_insights: {
    key_quotes?: string[];
    pain_points_addressed?: string[];
    goals_supported?: string[];
  };
  actions: Action[];
  action_count: number;
  average_score: number;
  current_streak: number;
  created_at: string;
  activated_at: string | null;
  completed_at: string | null;
}

const CATEGORY_ICONS: Record<string, string> = {
  mental_health: '🧠',
  physical_health: '💪',
  nutrition: '🥗',
  sleep: '😴',
  productivity: '📈',
  relationships: '❤️',
  financial: '💰',
  mindfulness: '🧘',
  exercise: '🏃',
  self_care: '💆',
  learning: '📚',
  social: '👥',
  general: '✨',
};

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const SCORE_LABELS: Record<number, { emoji: string; label: string; color: string }> = {
  1: { emoji: '😞', label: 'Struggling', color: '#dc3545' },
  2: { emoji: '😕', label: 'Difficult', color: '#fd7e14' },
  3: { emoji: '😐', label: 'Okay', color: '#FFC107' },
  4: { emoji: '🙂', label: 'Good', color: '#7cb342' },
  5: { emoji: '😄', label: 'Excellent!', color: '#4CAF50' },
};

const HabitDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { habitId } = useParams<{ habitId: string }>();
  const { logout } = useAuth();
  const [habit, setHabit] = useState<HabitDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const { toast, showError, closeToast } = useToast();

  // Check-in dialog state
  const [checkInOpen, setCheckInOpen] = useState(false);
  const [selectedAction, setSelectedAction] = useState<Action | null>(null);
  const [checkInScore, setCheckInScore] = useState(3);
  const [checkInNotes, setCheckInNotes] = useState('');
  const [checkInSubmitting, setCheckInSubmitting] = useState(false);

  // Dismiss feedback dialog state
  const [dismissOpen, setDismissOpen] = useState(false);
  const [dismissFeedback, setDismissFeedback] = useState('');
  const [dismissTags, setDismissTags] = useState<string[]>([]);

  const { confirmState, confirm, handleConfirm, handleCancel } = useConfirm();

  const loadHabit = useCallback(async () => {
    if (!habitId) return;
    try {
      setLoading(true);
      const res = await axios.get(API_ENDPOINTS.HABITS.DETAIL(habitId));
      setHabit(res.data);
    } catch (e: any) {
      showError(e?.response?.data?.detail || e?.message || 'Failed to load habit');
    } finally {
      setLoading(false);
    }
  }, [habitId]);

  useEffect(() => {
    loadHabit();
  }, [loadHabit]);

  const DISMISS_CANNED_OPTIONS = [
    'TOO HARD TO MAINTAIN',
    'NOT RELEVANT ANYMORE',
    'TOO MANY COMMITMENTS',
    'ACHIEVED MY GOAL',
    'TRYING A DIFFERENT APPROACH',
  ];

  const toggleDismissTag = (tag: string) => {
    setDismissTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  const handleStatusAction = async (action: 'activate' | 'pause' | 'dismiss') => {
    if (!habitId) return;

    // Dismiss uses its own dialog with feedback
    if (action === 'dismiss') {
      setDismissFeedback('');
      setDismissTags([]);
      setDismissOpen(true);
      return;
    }

    const confirmMessages: Record<string, string> = {
      activate: 'Are you sure you want to activate this habit?',
      pause: 'Are you sure you want to pause this habit?',
    };

    const confirmed = await confirm({ message: confirmMessages[action] });
    if (!confirmed) return;

    const actionUrls: Record<string, string> = {
      activate: API_ENDPOINTS.HABITS.ACTIVATE(habitId),
      pause: API_ENDPOINTS.HABITS.PAUSE(habitId),
    };

    try {
      setActionLoading(true);
      await axios.post(actionUrls[action]);
      loadHabit();
    } catch (e: any) {
      showError(e?.response?.data?.error || e?.response?.data?.detail || e?.message || 'Failed to update habit');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDismissConfirm = async () => {
    if (!habitId) return;

    // Build feedback: canned tags in brackets + free text
    const tagPart = dismissTags.map((t) => `[${t}]`).join(' ');
    const feedback = [tagPart, dismissFeedback.trim()].filter(Boolean).join(' ');

    try {
      setActionLoading(true);
      setDismissOpen(false);
      await axios.post(API_ENDPOINTS.HABITS.DISMISS(habitId), { feedback });
      navigate('/habits');
    } catch (e: any) {
      showError(e?.response?.data?.error || e?.response?.data?.detail || e?.message || 'Failed to dismiss habit');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDiscuss = async () => {
    if (!habitId || !habit || actionLoading) return;

    try {
      setActionLoading(true);
      const res = await axios.post(API_ENDPOINTS.HABITS.DISCUSS(habitId), {});
      const conversationId = res.data.conversation_id;
      navigate(`/chat/${conversationId}`);
    } catch (e: any) {
      showError(e?.response?.data?.error || e?.response?.data?.detail || e?.message || 'Failed to start discussion');
    } finally {
      setActionLoading(false);
    }
  };

  const isCheckedInToday = (action: Action): boolean => {
    if (!action.latest_progress) return false;
    const today = new Date().toISOString().split('T')[0];
    return action.latest_progress.check_in_date === today;
  };

  const openCheckIn = (action: Action) => {
    if (isCheckedInToday(action)) return;
    setSelectedAction(action);
    setCheckInScore(3);
    setCheckInNotes('');
    setCheckInOpen(true);
  };

  const handleCheckInSubmit = async (triggerDiscussion = false) => {
    if (!selectedAction || !habitId) return;

    try {
      setCheckInSubmitting(true);
      await axios.post(API_ENDPOINTS.HABITS.ACTION_CHECKIN(selectedAction.id), {
        score: checkInScore,
        notes: checkInNotes.trim(),
        trigger_discussion: triggerDiscussion,
      });

      if (triggerDiscussion) {
        // Keep dialog open so user sees "Starting discussion..." feedback
        const discussRes = await axios.post(API_ENDPOINTS.HABITS.DISCUSS(habitId), {
          action_id: selectedAction.id,
          context: `User just checked in and scored ${checkInScore}/5 for action "${selectedAction.title}". Their notes: ${checkInNotes.trim() || '(no notes)'}`,
        });
        setCheckInOpen(false);
        navigate(`/chat/${discussRes.data.conversation_id}`);
      } else {
        setCheckInOpen(false);
        loadHabit();
      }
    } catch (e: any) {
      showError(e?.response?.data?.error || e?.message || 'Failed to save check-in');
    } finally {
      setCheckInSubmitting(false);
    }
  };

  const getScoreColor = (score: number): string => {
    if (score >= 4) return '#4CAF50';
    if (score >= 3) return '#FFC107';
    return '#dc3545';
  };

  const scoreInfo = SCORE_LABELS[checkInScore] || SCORE_LABELS[3];

  if (loading) {
    return (
      <Box sx={{ height: 'var(--app-height, 100vh)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!habit) {
    return (
      <Box sx={{ height: 'var(--app-height, 100vh)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography color="error">Habit not found</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: 'var(--app-height, 100vh)', display: 'flex', flexDirection: 'column', bgcolor: '#F5F5F5' }}>
      <AppHeader title="Habit" />

      <Box sx={{ flex: 1, overflow: 'auto', p: 2, bgcolor: '#f5f5f5' }}>
        {/* Overview Card */}
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
              <Typography sx={{ fontSize: '2rem' }}>
                {CATEGORY_ICONS[habit.category] || '✨'}
              </Typography>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  {habit.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {habit.category_display} • {habit.timeframe_display}
                </Typography>
              </Box>
            </Box>

            <Typography variant="body1" sx={{ mb: 2 }}>
              {habit.description}
            </Typography>

            <Divider sx={{ my: 2 }} />

            {/* Stats */}
            <Grid container spacing={2} sx={{ textAlign: 'center' }}>
              <Grid item xs={4}>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 700, color: getScoreColor(habit.average_score) }}
                >
                  {habit.average_score.toFixed(1)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Ave Self Score
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {habit.current_streak}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Day Streak
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {habit.action_count}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Actions
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Rationale */}
        {habit.rationale && (
          <Card sx={{ mb: 2, bgcolor: '#E8F5E9', border: '1px solid #4CAF50' }}>
            <CardContent>
              <Typography variant="subtitle2" sx={{ color: '#2E7D32', mb: 1 }}>
                💡 Why this habit?
              </Typography>
              <Typography variant="body2" sx={{ color: '#1B5E20' }}>
                {habit.rationale}
              </Typography>
            </CardContent>
          </Card>
        )}

        {/* Actions */}
        <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 600 }}>
          Actions ({habit.action_count})
        </Typography>

        {habit.actions.map((action) => {
          const doneToday = isCheckedInToday(action);
          return (
            <Card
              key={action.id}
              sx={{
                mb: 1.5,
                cursor: doneToday ? 'default' : 'pointer',
                opacity: doneToday ? 0.7 : 1,
                borderLeft: action.is_due_today ? '4px solid #4CAF50' : 'none',
                '&:hover': { bgcolor: doneToday ? 'inherit' : '#fafafa' },
              }}
              onClick={() => openCheckIn(action)}
            >
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {action.title}
                  </Typography>
                  {doneToday ? (
                    <Chip label="✓ Done Today" size="small" sx={{ bgcolor: '#E8F5E9', color: '#2E7D32' }} />
                  ) : action.is_due_today ? (
                    <Chip label="Active Task Today" size="small" color="success" />
                  ) : null}
                </Box>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: action.description ? 0.5 : 1 }}>
                  {action.frequency === 'daily'
                    ? 'Daily'
                    : `Weekly: ${action.scheduled_days.map((d) => WEEKDAYS[d]).join(', ')}`}
                </Typography>

                {action.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontStyle: 'italic' }}>
                    {action.description}
                  </Typography>
                )}

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 700, color: getScoreColor(action.average_score) }}
                  >
                    {action.average_score.toFixed(1)}/5
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Ave Self Score
                  </Typography>
                </Box>

                {action.latest_progress && (
                  <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid #f0f0f0' }}>
                    <Typography variant="caption" color="text.secondary">
                      Last: {action.latest_progress.check_in_date} - Score: {action.latest_progress.score}/5
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          );
        })}

        {/* Action Buttons */}
        <Box sx={{ mt: 3, mb: 4 }}>
          {habit.status === 'suggested' && (
            <Button
              variant="contained"
              fullWidth
              size="large"
              startIcon={<PlayIcon />}
              onClick={() => handleStatusAction('activate')}
              disabled={actionLoading}
              sx={{ mb: 1.5 }}
            >
              Start This Habit
            </Button>
          )}

          {habit.status === 'active' && (
            <>
              <Button
                variant="contained"
                fullWidth
                size="large"
                startIcon={actionLoading ? <CircularProgress size={20} color="inherit" /> : <ChatIcon />}
                onClick={handleDiscuss}
                disabled={actionLoading}
                sx={{ mb: 1.5 }}
              >
                {actionLoading ? 'Starting discussion...' : 'Discuss with Jeff'}
              </Button>

              <Button
                variant="outlined"
                fullWidth
                startIcon={<PauseIcon />}
                onClick={() => handleStatusAction('pause')}
                disabled={actionLoading}
                sx={{ mt: 1.5 }}
              >
                Pause
              </Button>
            </>
          )}

          {habit.status === 'paused' && (
            <Button
              variant="contained"
              fullWidth
              size="large"
              startIcon={<PlayIcon />}
              onClick={() => handleStatusAction('activate')}
              disabled={actionLoading}
              sx={{ mb: 1.5 }}
            >
              Resume Habit
            </Button>
          )}

          {['suggested', 'active', 'paused'].includes(habit.status) && (
            <Button
              variant="outlined"
              color="error"
              fullWidth
              startIcon={<CloseIcon />}
              onClick={() => handleStatusAction('dismiss')}
              disabled={actionLoading}
              sx={{ mt: 1.5 }}
            >
              Dismiss Habit
            </Button>
          )}
        </Box>
      </Box>

      {/* Check-In Dialog */}
      <Dialog open={checkInOpen} onClose={() => setCheckInOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Check-in: {selectedAction?.title}</DialogTitle>
        <DialogContent>
          {selectedAction?.description && (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              {selectedAction.description}
            </Typography>
          )}
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              How did it go today?
            </Typography>
            <Typography variant="body2" sx={{ color: '#999', fontStyle: 'italic', mb: 1 }}>
              Self-score to monitor your progress
            </Typography>

            <Box sx={{ textAlign: 'center', my: 3 }}>
              <Typography sx={{ fontSize: '4rem', mb: 1 }}>{scoreInfo.emoji}</Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, color: scoreInfo.color }}>
                {scoreInfo.label}
              </Typography>
              <Typography variant="h6" sx={{ color: scoreInfo.color }}>
                {checkInScore}/5
              </Typography>
            </Box>

            <Slider
              value={checkInScore}
              onChange={(_e, newValue) => setCheckInScore(newValue as number)}
              step={1}
              marks
              min={1}
              max={5}
              sx={{
                color: scoreInfo.color,
                '& .MuiSlider-markLabel': { fontSize: '0.75rem' },
              }}
            />

            <TextField
              fullWidth
              multiline
              rows={3}
              label="Notes (optional)"
              placeholder="Add any reflections or thoughts about today..."
              value={checkInNotes}
              onChange={(e) => setCheckInNotes(e.target.value)}
              sx={{ mt: 3 }}
            />

            {checkInScore <= 2 && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  💭 Having a tough time? Talking through challenges with Jeff can help you find solutions.
                </Typography>
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={() => setCheckInOpen(false)} disabled={checkInSubmitting}>
            Cancel
          </Button>
          {checkInScore <= 2 ? (
            <>
              <Button
                onClick={() => handleCheckInSubmit(false)}
                disabled={checkInSubmitting}
              >
                {checkInSubmitting ? 'Saving...' : 'Just Save'}
              </Button>
              <Button
                variant="contained"
                onClick={() => handleCheckInSubmit(true)}
                disabled={checkInSubmitting}
                startIcon={checkInSubmitting ? <CircularProgress size={16} color="inherit" /> : undefined}
              >
                {checkInSubmitting ? 'Starting discussion...' : 'Save & Discuss'}
              </Button>
            </>
          ) : (
            <Button
              variant="contained"
              onClick={() => handleCheckInSubmit(false)}
              disabled={checkInSubmitting}
            >
              {checkInSubmitting ? 'Saving...' : 'Save Check-in'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Dismiss Feedback Dialog */}
      <Dialog open={dismissOpen} onClose={() => setDismissOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Dismiss Habit</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Why are you dismissing this habit? (optional)
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {DISMISS_CANNED_OPTIONS.map((tag) => (
              <Chip
                key={tag}
                label={tag}
                size="small"
                onClick={() => toggleDismissTag(tag)}
                sx={{
                  cursor: 'pointer',
                  bgcolor: dismissTags.includes(tag) ? '#1976d2' : '#e0e0e0',
                  color: dismissTags.includes(tag) ? '#fff' : 'text.primary',
                  fontWeight: dismissTags.includes(tag) ? 600 : 400,
                  '&:hover': { opacity: 0.85 },
                }}
              />
            ))}
          </Box>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Additional notes (optional)"
            placeholder="Any other thoughts on why this isn't working for you..."
            value={dismissFeedback}
            onChange={(e) => setDismissFeedback(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={() => setDismissOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDismissConfirm}
          >
            Dismiss Habit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog */}
      <ConfirmDialog
        open={confirmState.open}
        title={confirmState.title}
        message={confirmState.message}
        confirmLabel={confirmState.confirmLabel}
        cancelLabel={confirmState.cancelLabel}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />

      {/* Toast notifications */}
      <Toast
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={closeToast}
      />

      <ChatBottomNav />
    </Box>
  );
};

export default HabitDetailPage;


